'use client';

import { LAYOUT_SECTION_NAME } from 'shop-ui/constants';
import {
    type PageLayoutDetail,
    type LayoutNode,
    type BuyerSSRProps,
} from 'shop-ui/types';
import { getFeatureFlagData } from 'shop-ui/data';
import { getUIStyle as _getUIStyle, getUIContent as _getUIContent } from './utils';
import { withUIContext } from './withUIContext';
import { renderUIMap } from '../renderUIMap';

export type RenderElementProps<T> = {
    layoutId: string;
    component: LayoutNode;
    getUIStyle: ReturnType<typeof _getUIStyle>;
    getUIContent: ReturnType<typeof _getUIContent>;
    sectionName: LAYOUT_SECTION_NAME;
    __ssr: BuyerSSRProps<T>;
};

function recursiveRender<T>(props: RenderElementProps<T>) {
    const { layoutId, component, getUIStyle, getUIContent, sectionName, __ssr } = props;

    const { id, type, name, next, enable = true } = component;

    const Component = renderUIMap[type];

    if (!Component || !id) {
        return null;
    }

    const EnhancedComponent = withUIContext(Component);

    const children =
        next && Array.isArray(next) && next.length > 0
            ? next.map((_component) =>
                  recursiveRender({
                      layoutId,
                      component: _component,
                      getUIStyle,
                      getUIContent,
                      sectionName,
                      __ssr,
                  })
              )
            : (next as React.ReactNode);

    const _props = {
        layoutId,
        id,
        enable,
        type,
        feature: getFeatureFlagData(type, sectionName),
        style: getUIStyle(id),
        content: getUIContent(id),
        children,
        sectionName,
        __ssr,
    };

    return <EnhancedComponent key={`${layoutId}-${id}-${type}-${name}`} {..._props} />;
}

export function renderUI<T = unknown>(
    layoutId: string,
    layoutData: PageLayoutDetail['layout'],
    sectionName: LAYOUT_SECTION_NAME,
    __ssr: BuyerSSRProps<T>
) {
    if (!layoutData) {
        return null;
    }

    const { layout, style, content } = layoutData;

    const getUIStyle = _getUIStyle(style);

    const getUIContent = _getUIContent(content);

    if (!layout) {
        return null;
    }

    return Array.isArray(layout)
        ? layout.map((component) =>
              recursiveRender({
                  layoutId,
                  component,
                  getUIStyle,
                  getUIContent,
                  sectionName,
                  __ssr,
              })
          )
        : recursiveRender({
              layoutId,
              component: layout,
              getUIStyle,
              getUIContent,
              sectionName,
              __ssr,
          });
}
